import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, TrendingUp, Clock, DollarSign, Package, 
  Users, Building2, AlertTriangle, CheckCircle2, XCircle,
  Download, Eye, Filter, Calendar, Target, Award,
  ArrowUp, ArrowDown, RefreshCw, Settings, FileText, Edit
} from 'lucide-react';

const ReportsKPIsTab = ({ timeRange, region }) => {
  const [activeReport, setActiveReport] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const reports = [
    { id: 'overview', label: 'Overview', icon: BarChart3, description: 'Key performance indicators' },
    { id: 'procurement', label: 'Procurement', icon: Package, description: 'Lead time and vendor performance' },
    { id: 'logistics', label: 'Logistics', icon: TrendingUp, description: 'Delivery and transportation metrics' },
    { id: 'inventory', label: 'Inventory', icon: Target, description: 'Stock levels and turnover analysis' },
    { id: 'costs', label: 'Cost Analysis', icon: DollarSign, description: 'Cost breakdown and optimization' }
  ];

  // Mock data for overview KPIs
  const overviewKPIs = [
    {
      label: 'Procurement Lead Time',
      value: '8.5 days',
      change: '-12%',
      trend: 'down',
      target: '7 days',
      status: 'good'
    },
    {
      label: 'On-time Delivery Rate',
      value: '94.2%',
      change: '+2.1%',
      trend: 'up',
      target: '95%',
      status: 'good'
    },
    {
      label: 'Inventory Turnover',
      value: '6.8x',
      change: '+0.5x',
      trend: 'up',
      target: '7x',
      status: 'good'
    },
    {
      label: 'Cost Savings',
      value: 'SAR 245K',
      change: '+15%',
      trend: 'up',
      target: 'SAR 200K',
      status: 'excellent'
    }
  ];

  // Mock data for vendor performance
  const vendorPerformance = [
    {
      vendor: 'Al-Rashid Trading Co.',
      category: 'Kitchen Equipment',
      leadTime: 7,
      onTimeDelivery: 95,
      qualityScore: 92,
      costSavings: 45000,
      totalOrders: 156,
      performance: 'excellent'
    },
    {
      vendor: 'Tech Solutions Ltd.',
      category: 'IT Equipment',
      leadTime: 12,
      onTimeDelivery: 88,
      qualityScore: 85,
      costSavings: 32000,
      totalOrders: 89,
      performance: 'good'
    },
    {
      vendor: 'Office Supplies Pro',
      category: 'Office Supplies',
      leadTime: 5,
      onTimeDelivery: 92,
      qualityScore: 88,
      costSavings: 18000,
      totalOrders: 234,
      performance: 'good'
    }
  ];

  // Mock data for dead stock
  const deadStock = [
    {
      item: 'Old Model Refrigerator',
      category: 'Kitchen Equipment',
      quantity: 3,
      value: 37500,
      lastMovement: '2023-12-15',
      daysInStock: 90,
      risk: 'high'
    },
    {
      item: 'Discontinued Knife Set',
      category: 'Kitchen Equipment',
      quantity: 12,
      value: 5400,
      lastMovement: '2024-01-20',
      daysInStock: 55,
      risk: 'medium'
    },
    {
      item: 'Outdated Software License',
      category: 'IT Equipment',
      quantity: 5,
      value: 12500,
      lastMovement: '2024-02-10',
      daysInStock: 35,
      risk: 'medium'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      excellent: 'bg-green-100 text-green-800',
      good: 'bg-blue-100 text-blue-800',
      fair: 'bg-yellow-100 text-yellow-800',
      poor: 'bg-red-100 text-red-800',
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    return colors[status] || colors.fair;
  };

  const getTrendColor = (trend) => {
    return trend === 'up' ? 'text-green-600' : 'text-red-600';
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Key Performance Indicators</h3>
          <p className="text-sm text-gray-500 mt-1">Overview of supply chain performance metrics</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
          <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
            <Download className="w-4 h-4" />
            Export Report
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {overviewKPIs.map((kpi, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-2 rounded-lg ${getStatusColor(kpi.status).split(' ')[0]}`}>
                <Target className={`w-5 h-5 ${getStatusColor(kpi.status).split(' ')[1]}`} />
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(kpi.status)}`}>
                {kpi.status}
              </span>
            </div>
            
            <div>
              <p className="text-sm text-gray-500 mb-1">{kpi.label}</p>
              <p className="text-2xl font-bold text-gray-900 mb-2">{kpi.value}</p>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {getTrendIcon(kpi.trend)}
                  <span className={`ml-1 text-sm font-medium ${getTrendColor(kpi.trend)}`}>
                    {kpi.change}
                  </span>
                </div>
                <span className="text-xs text-gray-500">Target: {kpi.target}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Performance Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">Top Performing Vendors</h4>
            <Award className="w-5 h-5 text-emerald-600" />
          </div>
          
          <div className="space-y-3">
            {vendorPerformance.slice(0, 3).map((vendor, index) => (
              <div key={vendor.vendor} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-emerald-600">{index + 1}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{vendor.vendor}</p>
                    <p className="text-xs text-gray-500">{vendor.category}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{vendor.onTimeDelivery}%</p>
                  <p className="text-xs text-gray-500">On-time delivery</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Alerts */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">Recent Alerts</h4>
            <AlertTriangle className="w-5 h-5 text-orange-600" />
          </div>
          
          <div className="space-y-3">
            {[
              { message: 'Low stock alert: Commercial Oven', severity: 'medium', time: '2 hours ago' },
              { message: 'Late delivery: Tech Solutions Ltd.', severity: 'high', time: '4 hours ago' },
              { message: 'Quality issue reported: Office Supplies', severity: 'low', time: '6 hours ago' }
            ].map((alert, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <div className={`w-2 h-2 rounded-full ${
                  alert.severity === 'high' ? 'bg-red-500' : 
                  alert.severity === 'medium' ? 'bg-orange-500' : 'bg-yellow-500'
                }`} />
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{alert.message}</p>
                  <p className="text-xs text-gray-500">{alert.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderProcurement = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Procurement Reports</h3>
          <p className="text-sm text-gray-500 mt-1">Lead time analysis and vendor performance</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Download className="w-4 h-4" />
          Export Report
        </button>
      </div>

      {/* Vendor Performance Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  On-time Delivery
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quality Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost Savings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {vendorPerformance.map((vendor) => (
                <tr key={vendor.vendor} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{vendor.vendor}</p>
                      <p className="text-xs text-gray-500">{vendor.totalOrders} orders</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{vendor.category}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-900">{vendor.leadTime} days</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className="bg-emerald-500 h-2 rounded-full" 
                          style={{ width: `${vendor.onTimeDelivery}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-900">{vendor.onTimeDelivery}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{vendor.qualityScore}%</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-emerald-600">
                      SAR {vendor.costSavings.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vendor.performance)}`}>
                      {vendor.performance}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderInventory = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Inventory Reports</h3>
          <p className="text-sm text-gray-500 mt-1">Stock levels, turnover, and dead stock analysis</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Download className="w-4 h-4" />
          Export Report
        </button>
      </div>

      {/* Dead Stock Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900">Dead Stock Analysis</h4>
          <p className="text-sm text-gray-500 mt-1">Items with no movement for extended periods</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Days in Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {deadStock.map((item) => (
                <tr key={item.item} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-900">{item.item}</p>
                    <p className="text-xs text-gray-500">Last moved: {new Date(item.lastMovement).toLocaleDateString()}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{item.category}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{item.quantity}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900">
                      SAR {item.value.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{item.daysInStock} days</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.risk)}`}>
                      {item.risk}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderLogistics = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Logistics Reports</h3>
          <p className="text-sm text-gray-500 mt-1">Delivery and transportation performance metrics</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Download className="w-4 h-4" />
          Export Report
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-12">
          <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Logistics Performance</h3>
          <p className="text-gray-500 mb-6">Detailed logistics metrics and delivery performance analysis</p>
          <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
            View Full Report
          </button>
        </div>
      </div>
    </div>
  );

  const renderCosts = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Cost Analysis</h3>
          <p className="text-sm text-gray-500 mt-1">Cost breakdown and optimization opportunities</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Download className="w-4 h-4" />
          Export Report
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-12">
          <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Cost Analysis Report</h3>
          <p className="text-gray-500 mb-6">Detailed cost breakdown and optimization analysis</p>
          <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
            Generate Report
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Report Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex space-x-1">
          {reports.map((report) => {
            const Icon = report.icon;
            return (
              <button
                key={report.id}
                onClick={() => setActiveReport(report.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeReport === report.id
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={report.description}
              >
                <Icon className="w-4 h-4" />
                {report.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content based on active report */}
      {activeReport === 'overview' && renderOverview()}
      {activeReport === 'procurement' && renderProcurement()}
      {activeReport === 'logistics' && renderLogistics()}
      {activeReport === 'inventory' && renderInventory()}
      {activeReport === 'costs' && renderCosts()}
    </div>
  );
};

export default ReportsKPIsTab; 